import {html, type TemplateResult} from "lit";
import {customElement, property} from "lit/decorators.js";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {headline} from "../../../common/snippets/headline";
import type {EopPageOverlayLink} from "../pageOverlay/pageOverlayLink";
import type {Nullable} from "../../../bootstrap/common/types";
import {trackingTeaserInteract} from "../../../tracking/trackingAttributes";
import {UnLitElement} from "../../../common/elements";

export const IMAGE_TEASER_SIZE: Record<string, string> = {
    SMALL: "image-teaser-small",
    MEDIUM: "image-teaser-medium",
    LARGE: "image-teaser-large"
} as const;

export const IMAGE_TEASER_TEXT_CONFIGURATION: Record<string, string> = {
    ON_IMAGE: "image-teaser-with-content-on-image",
    BELOW_IMAGE: "image-teaser-with-content-below-image",
    WITHOUT_TEXT: "image-teaser-without-text"
} as const;

@customElement("eop-image-teaser")
export class ImageTeaser extends UnLitElement {

    @property({attribute: "link"})
    private link: string;
    @property({attribute: "text"})
    private text: string;
    @property({attribute: "headline"})
    private headline: string;
    @property({attribute: "hover-text"})
    private hoverText: string;
    @property({attribute: "headline-level", type: Number})
    private headlineLevel: 1 | 2 | 3 | 4 | 5 | 6 = 4;
    @property({attribute: "target"})
    private target: string = "_self";
    @property({attribute: "open-in-page-overlay", type: Boolean})
    private openInPageOverlay: boolean;
    @property({attribute: "image-src"})
    private imageSrc: string;
    @property({attribute: "text-configuration", converter: (value: Nullable<string>) => IMAGE_TEASER_TEXT_CONFIGURATION[value!]})
    private textPosition: string = IMAGE_TEASER_TEXT_CONFIGURATION.ON_IMAGE;
    @property({attribute: "image-size", converter: (value: Nullable<string>) => IMAGE_TEASER_SIZE[value!]})
    private imageSize: string = IMAGE_TEASER_SIZE.SMALL;
    @property({attribute: "video-src"})
    private videoSrc: string;
    @property({attribute: "tags"})
    private tags: HTMLElement;

    public constructor() {
        super();
    }

    public render(): TemplateResult {
        if (this.openInPageOverlay) {
            return html`
                <eop-page-overlay-link
                        class="teaser-link"
                        href=${this.link}
                        title=${this.hoverText}
                        ${trackingTeaserInteract(this.headline, this.link)}
                        tabindex="0"
                >
                    ${this.renderImageTeaser()}
                </eop-page-overlay-link>
            `;
        }
        return html`
            <a class="teaser-link"
               href=${this.link}
               target=${this.target}
               title=${this.hoverText}
               ${trackingTeaserInteract(this.headline, this.link)}
            >
                ${this.renderImageTeaser()}
            </a>
        `;
    }

    private renderImageTeaser(): TemplateResult {
        return html`
            <div class="image-teaser ${this.textPosition} ${this.imageSize}">
                ${this.renderImage()}
                ${this.renderContent()}
            </div>
        `;
    }

    public focusableElement(): HTMLElement {
        return this.renderRoot.querySelector<HTMLAnchorElement>("a") || this.renderRoot.querySelector<EopPageOverlayLink>("eop-page-overlay-link")!;
    }

    private renderContent(): Nullable<TemplateResult> {
        if (this.textPosition === IMAGE_TEASER_TEXT_CONFIGURATION.WITHOUT_TEXT) {
            return null;
        }
        return html`
            <div class="image-teaser-content">
                ${headline(`h${this.headlineLevel}`, this.headline, ["image-teaser-headline"])}
                ${this.renderText()}
                ${this.tags}
            </div>
        `;
    }

    private renderText(): Nullable<TemplateResult> {
        if (!this.text) {
            return null;
        }
        return html`
            <div class="image-teaser-text" data-role="content-sub-headline">${unsafeHTML(this.text)}</div>
        `;
    }

    private renderImage(): TemplateResult {
        return html`
            <div class="image-teaser-image">
                <eop-responsive-image .imageSrc=${this.imageSrc} .imageAlt=${this.hoverText}></eop-responsive-image>
                ${this.renderVideo()}
            </div>
        `;
    }

    private renderVideo(): Nullable<TemplateResult> {
        if (!this.videoSrc) {
            return null;
        }
        return html`
            <div class="image-teaser-video">
                <video class="video-loop" autoplay playsinline muted loop src=${this.videoSrc} preload="metadata" aria-label=""></video>
            </div>
        `;
    }
}